import {
  BINDINGS,
  isBoundField,
  type BindingDefinition,
  type InstanceKind,
  type Instances,
  type ModuleDefinitionField,
} from "shared"
import type { FieldValues } from "@rawa/form-modules/fieldValues"

/**
 * How the builder speaks about bound fields. The catalog names what each is;
 * these are the words around it, by the record a module's answers are about.
 */

/**
 * The record a module's bound fields are about. Only a module about a record
 * is offered any, and the server refuses one that is not, so this throws
 * rather than guess.
 */
export function recordOf(instances: Instances): InstanceKind {
  if (instances === "notSupported") {
    throw new Error("A module about no record holds no bound fields")
  }
  return instances.of
}

/**
 * What a preview starts its bound fields at: each binding's example, since a
 * preview reads no record. Custom fields start blank, to be tried out.
 */
export const exampleValues = (fields: ModuleDefinitionField[]): FieldValues =>
  Object.fromEntries(
    fields
      .filter(isBoundField)
      .map((field) => [field.id, BINDINGS[field.binding].example]),
  )

/** Whose value a bound field shows, for a module of this kind. */
export const RECORD_OWNER: Record<InstanceKind, string> = {
  session: "the session's",
}

/** What a binding's form mode means, as a tag and as a sentence. */
export const FORM_MODE: Record<
  BindingDefinition["formMode"],
  { tag: string; explained: string }
> = {
  displayOnly: {
    tag: "Display only",
    explained: "It is shown on the form, and can't be changed there.",
  },
  changeable: {
    tag: "Changeable",
    explained:
      "It starts with its current value, and a change made on the form is saved back.",
  },
}

/** Why a bound field is Required and locked there, for a module of this kind. */
export const requiredByRecord = (about: InstanceKind): string =>
  `Set by ${RECORD_OWNER[about]} record, which always holds a value, so it can't be changed here.`
